import React, { useCallback, useEffect, useRef, useState } from 'react';
import VideoService from '../../../../../../service/VideoService';
import styles from './Logos.module.scss';
import Icon from '../../../../../../components/Icon/Icon';
import cx from 'classnames';
import arrayMove from '../../../../../../utils/arrayUtil';
import SortableItemsContainer from '../../../../../../components/Sortable/SortableItemsContainer';
import SortableItem from '../../../../../../components/Sortable/SortableItem';
import ScrollContainer from '../../../../../../components/ScrollContainer/ScrollContainer';
import SortableItemHandle from '../../../../../../components/Sortable/SortableItemHandle';
import { useTranslation } from 'react-i18next';
import Spinner from '../../../../../../../common/components/Spinner';
import UploadLogoModal from '../UploadLogo/UploadLogoModal';
import { ToastNotification } from '../../../../../../components/Toast/Toast';
import { useUserContext } from '../../../../../../context/UserContext';
import DotsBtn from 'engage/components/Menu/DotsBtn';
import MenuItem from 'engage/components/Menu/MenuItem';
import Menu from 'engage/components/Menu/Menu';
import useFixedCardMenu from '../../../../../../features/Editor/hooks/useFixedCardMenu';
import { useAccordion } from '../../../../hooks/useAccordion';
import { BrandingAccordionHeader } from '../../../BrandingAccordionHeader/BrandingAccordionHeader';

const Logos = ({ handleOpenEditor, companyId }) => {
  const [logosList, setLogosList] = useState([]);
  const [selectedLogoId, setSelectedLogoId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const { updateUserAppConfig } = useUserContext();

  const { t } = useTranslation();

  const { isActive, toggleAccordionState, contentRef } = useAccordion(true);

  const scrollContainerRef = useRef(null);

  const { menuRef, toggleShowMenu, showMenu, menuCoordinates, onOpenMenu } = useFixedCardMenu({ containerRef: scrollContainerRef, offsetX: 200, offsetY: 200 });

  const handleSortStart = useCallback(
    ({ index }) => {
      const { id } = logosList.find((logo, logoIndex) => index === logoIndex) || {};
      setSelectedLogoId(id);
    },
    [logosList]
  );

  const handleSortEnd = useCallback(
    async ({ oldIndex, newIndex }) => {
      if (oldIndex !== newIndex) {
        try {
          setIsLoading(true);
          const movedList = arrayMove(logosList, oldIndex, newIndex);
          setLogosList(movedList);
          const payload = movedList.map(({ id }, index) => ({ id, sort: index }));
          const result = await VideoService.updateLogos(payload, companyId);

          const sortedList = result.sort((a, b) => a.sort - b.sort);
          setLogosList(sortedList);
          updateUserAppConfig();

          ToastNotification({
            message: t('company.settings.logos.sort.success'),
            iconName: 'success',
          });
        } catch (err) {
          console.log(err);
          ToastNotification({
            message: t('company.settings.logos.sort.error'),
          });
        }

        setIsLoading(false);
      }

      setSelectedLogoId(null);
    },
    [logosList, selectedLogoId]
  );

  const getLogosList = async () => {
    try {
      setIsLoading(true);
      const result = await VideoService.getLogos(companyId);
      if (result.data) {
        const sortedList = result.data.sort((a, b) => a.sort - b.sort);
        setLogosList(sortedList);
      }
    } catch (err) {
      console.log(err);
    }

    setIsLoading(false);
  };

  const handleDeleteLogo = async logoId => {
    try {
      const isConfirm = confirm(t('company.settings.logos.delete.confirmation'));

      if (!isConfirm) {
        return;
      }
      setIsLoading(true);
      await VideoService.deleteLogo(logoId, companyId);

      updateUserAppConfig();

      ToastNotification({
        message: t('company.settings.logos.delete.success'),
        iconName: 'success',
      });

      await getLogosList();
    } catch (err) {
      console.log(err);
      ToastNotification({
        message: t('company.settings.logos.delete.error'),
      });
    }

    setIsLoading(false);
  };

  useEffect(() => {
    getLogosList();
    scrollContainerRef.current?.scrollTo({
      left: 0,
      behavior: 'smooth',
    });
  }, [companyId]);

  return (
    <div className={styles.mainContainer}>
      <BrandingAccordionHeader isActive={isActive} onClick={toggleAccordionState} title={`${t('company.settings.logos.title')} (${logosList.length})`} description={t('company_settings.branding.logo_description')} />
      <div ref={contentRef} style={{ overflow: 'hidden' }}>
        <SortableItemsContainer
          axis="x"
          useDragHandle={true}
          helperClass={styles.sortable}
          updateBeforeSortStart={handleSortStart}
          onSortEnd={handleSortEnd}
          onSortOver={() => setSelectedLogoId(null)}
        >
          <ScrollContainer className={cx(styles.scrollContainer, { [styles.scrollContainerDisabled]: isLoading, [styles.hideScrollbar]: !isActive })} innerRef={scrollContainerRef}>
            <div className={styles.logoItem} role="button" tabIndex={0} onClick={() => setIsUploadModalOpen(true)}>
                <div className={cx(styles.logoImgContainer, styles.newLogoBtn)}>
                  <Icon src={'plus'} alt={t('logo_chooser.header')} />
                  <span className={styles.newLogoBtn}>{t('company.settings.logos.new_logo')}</span>
                </div>
            </div>
            {logosList.map((logo, index) => (
              <SortableItem key={logo.id} index={index} itemId={logo.id} tabIndex={0} disabled={isLoading}>
                <div key={logo?.id} className={cx(styles.logoItem, { [styles.logoItemActive]: logo?.id === selectedLogoId })}>
                  <SortableItemHandle>
                    <div className={cx(styles.logoDragContainer, { [styles.logoDragActive]: logo?.id === selectedLogoId })}>
                      <Icon src="dragDots" className={styles.dragIcon} />
                    </div>
                  </SortableItemHandle>
                  <div className={styles.logoImgContainer} role='button' tabIndex={0} onClick={() => handleOpenEditor(logo.id)}>
                    <img className={styles.logoImg} src={logo?.url} alt={t('logo_chooser.header')} />
                  </div>
                  <DotsBtn className={styles.dotsBtn} onClick={(event) => { event.stopPropagation(); onOpenMenu({ id: logo.id, event }) }} />
                </div>
              </SortableItem>
            ))}
          </ScrollContainer>
          {isLoading && isActive && <Spinner width="40px" height="40px" size="4px" className={styles.spinner} />}
        </SortableItemsContainer>
      </div>
      {showMenu.flag && <Menu ref={menuRef}
        customStyles={{
          width: 'fit-content',
          position: 'absolute',
          top: menuCoordinates.top,
          left: menuCoordinates.left,
          transform: menuCoordinates.transform,
        }}>
        <MenuItem onClick={() => { toggleShowMenu(); handleDeleteLogo(showMenu.id) }}>
          <Icon src="bin_red" className={styles.binActionIcon} /> <span style={{ color: 'rgb(203, 0, 0)' }}>{t('general.video_action_button.delete')}</span>
        </MenuItem>
      </Menu>}

      <UploadLogoModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleOpenEditor}
        companyId={companyId}
      />
    </div>
  );
};

export default Logos;
